import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  Box,
  Button,
  IconButton,
  TextField,
  InputAdornment,
  Typography,
} from "@mui/material";
import {
  FileDownloadOutlined as ExportIcon,
  GetApp as GetAppIcon,
  Refresh as RefreshIcon,
  Search as SearchIcon,
} from "@mui/icons-material";
import dayjs from "dayjs";
import { Workbook } from "exceljs";
import { saveAs } from "file-saver";
import { toast } from "react-hot-toast";
import { PaginatedTable } from "../../components";
import { baseAxios, Context } from "../../utils/config";

function formatDateTime(value) {
  if (!value) return "—";
  const d = dayjs(value);
  if (!d.isValid() || d.year() < 2000) return "—";
  return d.format("M/D/YY h:mm A");
}

function compareRows(a, b, field) {
  const va = a[field];
  const vb = b[field];
  if (field === "order_date") {
    return dayjs(va).valueOf() - dayjs(vb).valueOf();
  }
  if (typeof va === "number" || typeof vb === "number") {
    return (Number(va) || 0) - (Number(vb) || 0);
  }
  return String(va ?? "").localeCompare(String(vb ?? ""), undefined, {
    sensitivity: "base",
  });
}

function mapApiError(error) {
  if (error.response?.status === 403) {
    return "Not allowed to see reorder report data";
  }
  if (error.response?.status === 404) {
    return "Reorder report endpoint not found";
  }
  return error.response?.data?.message || "Error loading reorder report";
}

/**
 * Reorder Report — pending reorder orders with PDF download
 */
export default function ReOrderReport() {
  const { location: globalLocation } = useContext(Context);

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [hasRun, setHasRun] = useState(false);
  const [search, setSearch] = useState("");
  const [sortBy, setSortBy] = useState("order_date");
  const [sortOrder, setSortOrder] = useState("desc");

  const loadReport = useCallback(async () => {
    setLoading(true);
    setHasRun(true);
    try {
      const result = await baseAxios.get("/inventory/reports/reorders");
      const apiData = result.data?.data || result.data || [];
      setRows(Array.isArray(apiData) ? apiData : []);
    } catch (error) {
      setRows([]);
      toast.error(mapApiError(error), { duration: 3000 });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadReport();
  }, [loadReport, globalLocation?.location_id]);

  const filteredRows = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((row) => {
      const haystack = [
        row.order_number,
        row.reseller_name,
        formatDateTime(row.order_date),
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return haystack.includes(q);
    });
  }, [rows, search]);

  const sortedRows = useMemo(() => {
    const copy = [...filteredRows];
    copy.sort((a, b) => {
      const c = compareRows(a, b, sortBy);
      return sortOrder === "asc" ? c : -c;
    });
    return copy;
  }, [filteredRows, sortBy, sortOrder]);

  const handleSortChange = useCallback((field, order) => {
    setSortBy(field);
    setSortOrder(order);
  }, []);

  const noopPagination = useCallback(() => {}, []);

  const handleDownloadPdf = useCallback((pdfUrl, orderNumber) => {
    if (!pdfUrl) {
      toast.error("PDF not available for this order", { duration: 3000 });
      return;
    }
    const link = document.createElement("a");
    link.href = pdfUrl;
    link.download = `Order_${orderNumber || "reorder"}_PDF.pdf`;
    link.target = "_blank";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }, []);

  const handleExport = useCallback(async () => {
    if (!sortedRows.length) {
      toast.error("No data to export", { duration: 2500 });
      return;
    }

    try {
      const workbook = new Workbook();
      const worksheet = workbook.addWorksheet("Reorder Report");
      worksheet.columns = [
        { header: "Order Number", key: "order_number", width: 16 },
        { header: "Date", key: "order_date", width: 18 },
        { header: "Reseller Name", key: "reseller_name", width: 24 },
        { header: "PDF URL", key: "pdf_url", width: 40 },
      ];
      worksheet.getRow(1).font = { bold: true };
      sortedRows.forEach((row) => {
        worksheet.addRow({
          order_number: row.order_number,
          order_date: formatDateTime(row.order_date),
          reseller_name: row.reseller_name,
          pdf_url: row.pdf_url || "",
        });
      });

      const buffer = await workbook.xlsx.writeBuffer();
      saveAs(
        new Blob([buffer], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        }),
        `ReorderReport_${dayjs().format("YYYY-MM-DD")}.xlsx`
      );
    } catch {
      toast.error("Export failed", { duration: 3000 });
    }
  }, [sortedRows]);

  const columns = useMemo(
    () => [
      {
        id: "order_number",
        label: "Order Number",
        field: "order_number",
        minWidth: 150,
        render: (v) => v || "—",
      },
      {
        id: "order_date",
        label: "Date",
        field: "order_date",
        minWidth: 140,
        render: (v) => formatDateTime(v),
      },
      {
        id: "reseller_name",
        label: "Reseller Name",
        field: "reseller_name",
        minWidth: 200,
        render: (v) => v || "—",
      },
      {
        id: "pdf_url",
        label: "PDF Download",
        field: "pdf_url",
        minWidth: 150,
        sortable: false,
        render: (pdfUrl, row) => (
          <Button
            variant="contained"
            color="primary"
            size="small"
            startIcon={<GetAppIcon />}
            onClick={(e) => {
              e.stopPropagation();
              handleDownloadPdf(pdfUrl, row?.order_number);
            }}
            disabled={!pdfUrl}
            sx={{
              minWidth: 120,
              textTransform: "none",
              fontWeight: 700,
              borderRadius: "6px",
              boxShadow: "none",
              "&:hover": { boxShadow: "none" },
            }}
          >
            Download
          </Button>
        ),
      },
    ],
    [handleDownloadPdf]
  );

  return (
    <Box sx={{ px: 8, py: 3 }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: 2,
          mt: 1.5,
          mb: 2,
        }}
      >
        <Box>
          <Typography fontWeight={600} sx={{ fontSize: "1.5rem" }}>
            Reorder Report
          </Typography>
          <Typography variant="body2" color="text.secondary">
            View reorder orders and download order PDFs.
          </Typography>
        </Box>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            flexWrap: "wrap",
          }}
        >
          <TextField
            size="small"
            placeholder="Search orders..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            sx={{
              minWidth: 220,
              "& .MuiOutlinedInput-root": {
                backgroundColor: "background.paper",
                borderRadius: "6px",
              },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" color="action" />
                </InputAdornment>
              ),
            }}
          />
          <IconButton
            aria-label="Refresh report"
            onClick={loadReport}
            disabled={loading}
            size="medium"
            sx={{
              border: "1px solid",
              borderColor: "divider",
              bgcolor: "background.paper",
              borderRadius: "6px",
              "&:hover": { bgcolor: "grey.100" },
            }}
          >
            <RefreshIcon />
          </IconButton>
          <Button
            variant="outlined"
            color="secondary"
            startIcon={<ExportIcon />}
            onClick={handleExport}
            disabled={loading || !sortedRows.length}
            sx={{
              minHeight: 40,
              textTransform: "none",
              fontWeight: 600,
              borderRadius: "6px",
            }}
          >
            Export
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={loadReport}
            disabled={loading}
            sx={{
              minHeight: 40,
              px: 2,
              textTransform: "none",
              fontWeight: 700,
              borderRadius: "6px",
              boxShadow: "none",
              "&:hover": { boxShadow: "none" },
            }}
          >
            {loading ? "Loading…" : "Run Report"}
          </Button>
        </Box>
      </Box>

      <PaginatedTable
        columns={columns}
        rows={sortedRows}
        totalRowCount={sortedRows.length}
        loading={loading}
        onPaginationChange={noopPagination}
        onSortChange={handleSortChange}
        initialOrderBy="order_date"
        initialOrder="desc"
        searchQuery={`${search}|${globalLocation?.location_id ?? "all"}|${rows.length}`}
        clientSidePagination
        skeletonHeight="40px"
        getRowKey={(row, index) => row.order_id ?? `reorder-${index}`}
        emptyMessage={
          hasRun ? "No reorder data available" : "Click Run Report to load data"
        }
      />
    </Box>
  );
}
